import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import { ChevronRight } from './icons';
import type { Coin } from './TokenSheet';
import { useWallet } from '../../hooks/useWallet';
import { addressDisplayName } from '../../lib/walletNames';

const short = (a: string) => (a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);

type Props = {
  open: boolean;
  onClose: () => void;
  coin: Coin;
  onSelect: (address: string) => void;
  title: string;
  /** Show a back arrow (e.g. to return to the token/network step in a flow). */
  onBack?: () => void;
};

// Symbol-aware: BTC needs 8 fraction digits (6 would hide sat-level balances).
const fmtAmount = (n: number, symbol: string) =>
  n.toLocaleString('en-US', { maximumFractionDigits: symbol === 'BTC' ? 8 : 6 });

export function AddressSheet({ open, onClose, coin, onSelect, title, onBack }: Props) {
  const { t } = useTranslation();
  const { wallet, balances } = useWallet();
  const addresses = (wallet?.addresses ?? []).filter((a) => a.chain === coin.chain);

  // Same naming as the coin detail (shared helper) so both screens agree.
  const displayName = (a: { label: string | null; derivationIndex: number }) =>
    addressDisplayName(a, t('nav.wallet'));

  // This address's spendable balance for the selected coin (token × chain).
  const balanceFor = (eoaAddress: string) =>
    balances
      .filter((b) => b.chain === coin.chain && b.token === coin.symbol && b.address === eoaAddress)
      .reduce((sum, b) => sum + Number(b.raw) / 10 ** b.decimals, 0);

  return (
    <BottomSheet open={open} onClose={onClose} onBack={onBack} title={title}>
      <div className="mt-4 overflow-hidden rounded-[20px] bg-[#f7f7f8]">
        {addresses.length === 0 ? (
          <div className="p-4 text-[14px] text-muted">
            {t('common.noAddress', { network: coin.network })}
          </div>
        ) : (
          addresses.map((a, i) => (
            <button
              key={i}
              onClick={() => onSelect(a.eoaAddress)}
              className={`flex w-full items-center gap-3 p-4 text-left active:bg-black/[0.03] ${
                i > 0 ? 'border-t border-black/[0.06]' : ''
              }`}
            >
              <coin.Icon size={44} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[16px] font-medium text-ink">{displayName(a)}</div>
                <div className="text-[13px] tabular-nums text-muted">
                  {fmtAmount(balanceFor(a.eoaAddress), coin.symbol)} {coin.symbol}
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="text-[13px] tabular-nums text-muted">{short(a.eoaAddress)}</span>
                <span className="text-[11px] text-muted">
                  {coin.symbol} • {coin.network}
                </span>
              </div>
              <ChevronRight size={18} className="text-muted" />
            </button>
          ))
        )}
      </div>
    </BottomSheet>
  );
}
